import {
  Accordion,
  Box,
  Group,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { Lock, Info } from 'react-feather';
import { modals } from '@mantine/modals';
import {
  COACHING_SUB_TYPE_OPTIONS,
  MODALITY_LABELS,
  LIVE_SESSION_DELIVERY_LABEL,
  LIVE_SESSION_DELIVERY_TOOLTIP,
  LIVE_SESSION_DELIVERY_OFF_CONFIRM,
} from '../../../../config/cohortConfig';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import type { CoachingSubType } from '../../../../types';
import type { ModalityConfigRow } from './editCohortTypes';

// MED-955 (AC2) — suggested copy when the sub-type is locked because session
// leaders have already been designated.
const SESSION_LEADERS_LOCK_COPY =
  'Modality type cannot be changed after session leaders have been assigned.';
// MED-848 (AC9) — pre-existing copy when the sub-type is locked because
// announcement / prep emails have already gone out.
const INVITES_SENT_LOCK_COPY = 'Sub-type can no longer be changed for this modality.';
const INVITES_SENT_TOOLTIP_COPY =
  'Locked because session prep emails have already been sent for this modality';

interface ModalitySettingsSectionProps {
  modalityConfigs: ModalityConfigRow[];
  updateModalitySettingsField: (
    modalityId: string,
    field:
      | 'coaching_sub_type'
      | 'ai_features_enabled'
      | 'live_session_delivery_enabled'
      | 'commitment_tracking_enabled',
    value: CoachingSubType | boolean | null,
  ) => void;
}

// MED-848 — accordion section for AC8/AC9 post-creation editing of coaching
// sub-type (group_coaching only) and AI features toggle (group_coaching and
// workshop). 1:1 modalities have no controls and are filtered out.
export function ModalitySettingsSection({
  modalityConfigs,
  updateModalitySettingsField,
}: ModalitySettingsSectionProps) {
  // MED-955 (AC6) — the new "session leaders designated" lock condition only
  // applies when live_session_delivery is on. The pre-existing invites_sent
  // lock (MED-848 AC9) is unconditional.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  // MED-948 (E1.10a) AC8 — turning the gate off while AI features are enabled
  // prompts for confirmation, then resets AI features to off (the hook clears
  // ai_features_enabled when the gate is set false, and the backend enforces
  // the same invariant).
  const handleLiveSessionDeliveryToggle = (config: ModalityConfigRow, checked: boolean) => {
    if (!checked && config.ai_features_enabled) {
      modals.openConfirmModal({
        title: 'Turn off Live Session Delivery in SGOS?',
        children: <Text size="sm">{LIVE_SESSION_DELIVERY_OFF_CONFIRM}</Text>,
        labels: { confirm: 'Turn off', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () =>
          updateModalitySettingsField(config.id, 'live_session_delivery_enabled', false),
      });
      return;
    }
    updateModalitySettingsField(config.id, 'live_session_delivery_enabled', checked);
  };

  const visibleConfigs = modalityConfigs.filter(
    (m) => m.modality_type === 'group_coaching' || m.modality_type === 'workshop',
  );
  if (visibleConfigs.length === 0) return null;

  return (
    <Accordion.Item value="modality-settings">
      <Accordion.Control>
        <Text fw={600}>Modality Settings</Text>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="md">
          {visibleConfigs.map((config) => {
            const isGroupCoaching = config.modality_type === 'group_coaching';
            // MED-955 (AC1/3) — session-leaders lock takes precedence over the
            // invites_sent lock for messaging because for peer_led cohorts it
            // fires first chronologically and is the more specific cause.
            const lockedByLeaders =
              isGroupCoaching && liveSessionDeliveryEnabled && config.session_leaders_designated;
            const lockedByInvites = isGroupCoaching && config.invites_sent;
            const subTypeLocked = lockedByLeaders || lockedByInvites;
            const lockTooltip = lockedByLeaders
              ? SESSION_LEADERS_LOCK_COPY
              : INVITES_SENT_TOOLTIP_COPY;
            const lockMessage = lockedByLeaders
              ? SESSION_LEADERS_LOCK_COPY
              : INVITES_SENT_LOCK_COPY;

            return (
              <Box
                key={config.id}
                p="md"
                style={{
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  borderRadius: '8px',
                  border: '1px solid var(--mantine-color-gray-3)',
                }}
              >
                <Text size="sm" fw={600} mb="md">
                  {MODALITY_LABELS[config.modality_type as keyof typeof MODALITY_LABELS] ||
                    config.modality_type}
                </Text>

                {isGroupCoaching && (
                  <Box mb="md">
                    <Group gap={6} mb="xs">
                      <Text size="xs" fw={600}>
                        Coaching sub-type
                      </Text>
                      {subTypeLocked && (
                        <Tooltip label={lockTooltip} withArrow>
                          <Lock size={12} color="#666" aria-label="Locked" />
                        </Tooltip>
                      )}
                    </Group>
                    <SegmentedControl
                      value={config.coaching_sub_type ?? ''}
                      onChange={(value) =>
                        updateModalitySettingsField(
                          config.id,
                          'coaching_sub_type',
                          value as CoachingSubType,
                        )
                      }
                      data={COACHING_SUB_TYPE_OPTIONS.map((o) => ({
                        value: o.value,
                        label: o.label,
                      }))}
                      color="medleyGreen"
                      fullWidth
                      disabled={subTypeLocked}
                    />
                    {subTypeLocked && (
                      <Text size="xs" c="dimmed" mt={4}>
                        {lockMessage}
                      </Text>
                    )}
                  </Box>
                )}

                {isGroupCoaching ? (
                  // MED-948 (E1.10a) — Group Coaching: Live Session Delivery gate
                  // (AC1) reveals the AI toggle (AC3/AC4). The section itself is
                  // already hidden when the system flag is off (MED-887).
                  <Stack gap="md">
                    <Switch
                      color="medleyGreen"
                      label={
                        <Group gap={6} wrap="nowrap" align="center">
                          <Text size="sm">{LIVE_SESSION_DELIVERY_LABEL}</Text>
                          <Tooltip
                            label={LIVE_SESSION_DELIVERY_TOOLTIP}
                            multiline
                            w={260}
                            withArrow
                          >
                            <Box
                              component="span"
                              style={{ display: 'inline-flex', cursor: 'help', color: '#666' }}
                              aria-label={LIVE_SESSION_DELIVERY_TOOLTIP}
                            >
                              <Info size={14} />
                            </Box>
                          </Tooltip>
                        </Group>
                      }
                      checked={config.live_session_delivery_enabled}
                      onChange={(event) =>
                        handleLiveSessionDeliveryToggle(config, event.currentTarget.checked)
                      }
                    />
                    {config.live_session_delivery_enabled && (
                      <Switch
                        color="medleyGreen"
                        label="AI features"
                        description="Enable AI-powered facilitation features for this modality."
                        checked={config.ai_features_enabled}
                        onChange={(event) =>
                          updateModalitySettingsField(
                            config.id,
                            'ai_features_enabled',
                            event.currentTarget.checked,
                          )
                        }
                      />
                    )}
                    {/* MED-1069 (E1.10c) — Commitment Tracking. INDEPENDENT of the
                        Live Session Delivery gate (AC3): rendered unconditionally
                        for Group Coaching, NOT nested under live delivery like AI
                        features. Group coaching only (AC5). */}
                    <Switch
                      color="medleyGreen"
                      label="Commitment Tracking"
                      description="Lets participants set a commitment each session and reflect on the prior one. Off by default."
                      checked={config.commitment_tracking_enabled}
                      onChange={(event) =>
                        updateModalitySettingsField(
                          config.id,
                          'commitment_tracking_enabled',
                          event.currentTarget.checked,
                        )
                      }
                    />
                  </Stack>
                ) : (
                  // Workshop AI toggle — independent of the gate (AC10).
                  <Switch
                    color="medleyGreen"
                    label="AI features"
                    description="Enable AI-powered facilitation features for this modality."
                    checked={config.ai_features_enabled}
                    onChange={(event) =>
                      updateModalitySettingsField(
                        config.id,
                        'ai_features_enabled',
                        event.currentTarget.checked,
                      )
                    }
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
